import React, { ChangeEvent, useState, useEffect } from "react";
import axios from "axios";
import { Trash2 } from "lucide-react";
import { Door } from "./types";

interface LockType {
  id: number;
  type_code: string;
  name: string;
  icon_path: string | null;
  display_order: number;
  is_active: number | boolean;
}

interface DoorTableProps {
  doors: Door[];
  handleRemoveDoorx: (id: number) => void;
  handleDoorChange: (index: number, field: keyof Door, value: string | number) => void;
  handleInputChange: (
    index: number,
    field: keyof Door,
    value: string | number,
    numb: number,
    step: number,
    name: string,
    innen: number,
    auben: number
  ) => void;
  getDefaultLockValues: (lockType: string) => { lockOutside: number; lockInside: number };
  backendUrl: string;
  highlightedIndex?: number | null;
}

const DoorTable: React.FC<DoorTableProps> = ({
  doors,
  handleRemoveDoorx,
  handleDoorChange,
  handleInputChange,
  getDefaultLockValues,
  backendUrl,
  highlightedIndex = null,
}) => {
  const [lockTypes, setLockTypes] = useState<LockType[]>([]);

  useEffect(() => {
    const fetchLockTypes = async () => {
      try {
        const response = await axios.get(`${backendUrl}/api/v1/configurator/lock-types`);
        // Filtruj tylko aktywne typy i posortuj po display_order
        const activeLockTypes = response.data
          .filter((lt: LockType) => lt.is_active)
          .sort((a: LockType, b: LockType) => a.display_order - b.display_order);
        setLockTypes(activeLockTypes);
      } catch (error) {
        console.error("Error fetching lock types:", error);
      }
    };
    fetchLockTypes();
  }, [backendUrl]);

  // Kolumny wiersza (nazwa, Stück, typ, długości, odstęp pod kolumną klucza) to ścieżki
  // siatki --door-cols z 1/style.css - te same co w nagłówku DoorPanel.tsx
  return (
    <div className="door-table">
      {doors.map((el, index) => (
        <div key={index} className={`door-table-row${highlightedIndex === index ? " is-highlighted" : ""}`}>
        <div className="door-name-wrapper">
          {/* Numer drzwi przy wierszu matrycy - ułatwia trafienie w wiersz */}
          <span className="door-table-index" aria-hidden="true">{index + 1}</span>
          {/* Kosz zamiast czerwonego X - krzyżyk w matrycy oznacza uprawnienie */}
          <span
            role="button"
            className="table-trash"
            title="Tür löschen"
            aria-label="Tür löschen"
            onClick={() => handleRemoveDoorx(Number(el.id))}
          >
            <Trash2 size={18} strokeWidth={2} />
          </span>
          <input
            type="text"
            placeholder="Name"
            value={el.doorName}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              handleDoorChange(index, "doorName", e.target.value)
            }
          />  </div>
          <input
           type="number"
  placeholder=""
  className="inputCenter lock-amount-input"
  value={el.lockAmount}
  onChange={(e: ChangeEvent<HTMLInputElement>) =>
    handleDoorChange(
      index,
      "lockAmount",
      parseInt(e.target.value, 10)
    )
  }
          />
          <div className="door-type">
            <img src={`${backendUrl}${lockTypes.find(lt => lt.type_code === el.lockType)?.icon_path || `/img/Types/${encodeURIComponent(el.lockType)}.png`}`} alt={el.lockType} style={{ width: "20px", height: "auto" }}></img>
            <select
              style={{
                width: "145px",
                border: "none",
                backgroundColor: "#F3F3F3",
                borderBottom: "#B1B1C5 solid 1px",
              }}
              value={el.lockType}
              onChange={(e) =>
                handleDoorChange(index, "lockType", e.target.value)
              }
            >
              {lockTypes.map((lockType) => (
                <option key={lockType.id} value={lockType.type_code}>
                  {lockType.name}
                </option>
              ))}
            </select>
          </div>
          <div className="door-lengths">
            <div className="door-length">
              <div className="inputValueChangerForm">
                <div
                  onClick={() =>
                    handleInputChange(
                      index,
                      "lockOutside",
                      "",
                      el.lockOutside,
                      -5,
                      el.lockType,
                      el.lockInside,
                      el.lockOutside
                    )
                  }
                >
                  <img
                    src={`${backendUrl}/img/configurator/minus.svg`}
                    alt="minus"
                    style={{ width: "12px" }}
                  />
                </div>
              </div>
              <input
                style={{
                  width: "30px",
                  border: "none",
                  backgroundColor: "#F3F3F3",
                  borderBottom: "#B1B1C5 solid 1px",
                  textAlign: "center",
                }}
                type="number"
                readOnly={true}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  handleDoorChange(
                    index,
                    "lockOutside",
                    parseInt(e.target.value, 10)
                  )
                }
                className="inputCenter"
                value={el.lockOutside === 0 ? "" : el.lockOutside.toString()}
                placeholder={
                  getDefaultLockValues(el.lockType).lockOutside?.toString() || ""
                }
                disabled={
                  el.lockType === "VHS" || el.lockType === "Briefkastenzylinder"
                    ? true
                    : false
                }
              />
              <div className="inputValueChangerForm">
                <div
                  onClick={() =>
                    handleInputChange(
                      index,
                      "lockOutside",
                      "",
                      el.lockOutside,
                      5,
                      el.lockType,
                      el.lockInside,
                      el.lockOutside
                    )
                  }
                >
                  <img
                    src={`${backendUrl}/img/configurator/plus.svg`}
                    alt="minus"
                    style={{ width: "12px" }}
                  />
                </div>
              </div>
            </div>
            <div className="door-length">
              <div className="inputValueChangerForm">
                <div
                  onClick={() =>
                    handleInputChange(
                      index,
                      "lockInside",
                      "",
                      el.lockInside,
                      -5,
                      el.lockType,
                      el.lockInside,
                      el.lockOutside
                    )
                  }
                >
                  <img
                    src={`${backendUrl}/img/configurator/minus.svg`}
                    alt="minus"
                    style={{ width: "12px" }}
                  />
                </div>
              </div>
              <input
                style={{
                  width: "30px",
                  border: "none",
                  backgroundColor: "#F3F3F3",
                  borderBottom: "#B1B1C5 solid 1px",
                  textAlign: "center",
                }}
                type="number"
                readOnly={true}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  handleDoorChange(
                    index,
                    "lockInside",
                    parseInt(e.target.value, 10)
                  )
                }
                className="inputCenter"
                value={el.lockInside === 0 ? "" : el.lockInside.toString()}
                placeholder={
                  getDefaultLockValues(el.lockType).lockInside?.toString() || ""
                }
                disabled={
                  el.lockType === "HPZ" ||
                  el.lockType === "VHS" ||
                  el.lockType === "Briefkastenzylinder"
                    ? true
                    : false
                }
              />
              <div className="inputValueChangerForm">
                <div
                  onClick={() =>
                    handleInputChange(
                      index,
                      "lockInside",
                      "",
                      el.lockInside,
                      5,
                      el.lockType,
                      el.lockInside,
                      el.lockOutside
                    )
                  }
                >
                  <img
                    src={`${backendUrl}/img/configurator/plus.svg`}
                    alt="minus"
                    style={{ width: "12px" }}
                  />
                </div>
              </div>
            </div>
          </div>
           <div className="door"></div>
        </div>
      ))}
    </div>
  );
};

export default DoorTable;
